import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LockIcon from '@mui/icons-material/Lock';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { useCart } from '../contexts/CartContext';
import api from '../services/api';

// Définition du type Card pour inclure la propriété description
interface Card {
  id: string;
  name: string;
  price: number;
  image: string;
  description: string;
}

const steps = ['Panier', 'Livraison', 'Paiement', 'Confirmation'];

export const CheckoutPage: React.FC = () => {
  const { items, total, clearCart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const createOrder = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      const orderData = {
        items: items.map((item) => ({
          productId: item.id,
          quantity: 1,
          price: item.price,
        })),
        shippingAddress: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country,
        }),
        status: 'pending',
        totalAmount: total,
      };

      const { data: order } = await api.post('/orders', orderData);
      console.log(order);
      clearCart();
      navigate('/store');
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la création de la commande'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      createOrder();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const getStepContent = (step: number): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Récapitulatif de votre commande
            </Typography>
            <List>
              {items.map((item, index) => (
                <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }} key={item.id}>
                  <ListItem
                    sx={{
                      py: 2,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.05)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px 0 rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={'/assets/img/card.png'}
                        alt={item.name}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.1)',
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {item.name}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                        {item.price}€
                      </Typography>
                      <Tooltip title="Supprimer">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => removeFromCart(item.id)}
                          sx={{
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                </Zoom>
              ))}
            </List>
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: theme.palette.primary.main + '10',
              }}
            >
              <Typography variant="h6">Total</Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                {total}€
              </Typography>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Informations de livraison
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}
            >
              <TextField
                required
                fullWidth
                label="Prénom"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  },
                }}
              />
              <Box>
                <TextField
                  required
                  fullWidth
                  label="Nom"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  required
                  fullWidth
                  label="Adresse"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </Box>
              <Box>
                <TextField
                  required
                  fullWidth
                  label="Ville"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                />
              </Box>
              <Box>
                <TextField
                  required
                  fullWidth
                  label="Code postal"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  required
                  fullWidth
                  label="Pays"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                />
              </Box>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Informations de paiement
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}
            >
              <TextField
                required
                fullWidth
                label="Numéro de carte"
                name="cardNumber"
                value={formData.cardNumber}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <CreditCardIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  },
                }}
              />
              <TextField
                required
                fullWidth
                label="Nom sur la carte"
                name="cardName"
                value={formData.cardName}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  },
                }}
              />
              <Box>
                <TextField
                  required
                  fullWidth
                  label="Date d'expiration"
                  name="expiryDate"
                  placeholder="MM/AA"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                />
              </Box>
              <Box>
                <TextField
                  required
                  fullWidth
                  label="CVV"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleInputChange}
                />
              </Box>
            </Box>
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: theme.palette.primary.main + '10',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <LockIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Vos informations de paiement sont sécurisées et cryptées
              </Typography>
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Zoom in={true}>
              <CheckCircleIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
            </Zoom>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Commande confirmée !
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Merci pour votre achat. Un email de confirmation vous sera envoyé dans quelques
              minutes.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Chip
                icon={<LocalShippingIcon />}
                label="Livraison estimée: 3-5 jours"
                color="primary"
                sx={{
                  mr: 1,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              />
              <Chip
                icon={<PaymentIcon />}
                label={`Total: ${total}€`}
                color="success"
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              />
            </Box>
          </Box>
        );
      default:
        return <Typography>Étape inconnue</Typography>;
    }
  };

  return (
    <PageTransition>
      <Box
        sx={{
          py: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px 0 rgba(0,0,0,0.1)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 12px 48px 0 rgba(0,0,0,0.15)',
              },
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <ShoppingCartIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Finaliser la commande
              </Typography>
            </Box>

            <Stepper
              activeStep={activeStep}
              sx={{
                mb: 4,
                '& .MuiStepLabel-root .Mui-active': {
                  color: theme.palette.primary.main,
                },
                '& .MuiStepLabel-root .Mui-completed': {
                  color: theme.palette.success.main,
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Fade in={true} timeout={500}>
              <Box>{getStepContent(activeStep)}</Box>
            </Fade>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0 || isSubmitting}
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateX(-5px)',
                  },
                }}
              >
                Retour
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={isSubmitting}
                endIcon={
                  activeStep === steps.length - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />
                }
                sx={{
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontWeight: 'bold',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateX(5px)',
                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.25)',
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  'Terminer'
                ) : (
                  'Suivant'
                )}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </PageTransition>
  );
};
